import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Mail, Copy, CheckCircle2, AlertCircle, RefreshCw, Trash2, BellRing, Send, Users, GraduationCap, Plus, Target, Paperclip, Upload, FileText, Sparkles, ExternalLink, ThumbsUp, EyeOff, Bell } from 'lucide-react';
import { senderDomain, deadlineNudge, cpd as cpdApi, cpdExtract, notifPrefs } from '@/lib/api';
import { toast } from 'sonner';
import MessagingCard from './MessagingCard';
import BrandingCard from './BrandingCard';
import XeroCard from './XeroCard';
import ReminderPrefsCard from './ReminderPrefsCard';
import ReminderTemplatesCard from './ReminderTemplatesCard';
import FleetAlertsCard from './FleetAlertsCard';
import FleetSlaCard from './FleetSlaCard';

export default function SettingsPage() {
  return (
    <div className="p-6">
      <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Settings</h1>
      <div className="text-sm text-slate-500">Firm profile, integrations, compliance preferences.</div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card className="border-slate-200"><CardContent className="p-6">
          <div className="text-sm font-semibold text-[#1A3A5C]">Firm profile</div>
          <div className="mt-4 space-y-4">
            <div><Label>Firm name</Label><Input defaultValue="Nair Migration Advisory" className="mt-1" /></div>
            <div><Label>ABN</Label><Input defaultValue="51 824 753 556" className="mt-1" /></div>
            <div><Label>Trust account BSB</Label><Input defaultValue="062-900" className="mt-1" /></div>
            <div><Label>Trust account number</Label><Input defaultValue="1044-2219" className="mt-1" /></div>
          </div>
        </CardContent></Card>

        <Card className="border-slate-200"><CardContent className="p-6">
          <div className="text-sm font-semibold text-[#1A3A5C]">Compliance</div>
          <div className="mt-4 space-y-4">
            {[
              { l: 'Require 2FA for all staff', on: true },
              { l: 'Client statutory declaration on document upload', on: true },
              { l: 'Pre-transfer consent gate (trust → operating)', on: true },
              { l: '7-year retention with auto-purge', on: true },
              { l: 'CPD reminders', on: true },
              { l: 'AML/CTF Tranche-2 KYC capture', on: false },
            ].map((r) => (
              <div key={r.l} className="flex items-center justify-between">
                <span className="text-sm text-[#1A3A5C]">{r.l}</span>
                <Switch defaultChecked={r.on} />
              </div>
            ))}
          </div>
        </CardContent></Card>
      </div>

      <div className="mt-6">
        <SenderDomainCard />
      </div>

      <div className="mt-6">
        <DeadlineNudgeCard />
      </div>

      <div className="mt-6">
        <NotificationPrefsCard />
      </div>

      <div className="mt-6">
        <MessagingCard />
      </div>

      <div className="mt-6">
        <BrandingCard />
      </div>

      <div className="mt-6">
        <XeroCard />
      </div>

      <div className="mt-6">
        <ReminderPrefsCard />
      </div>

      <div className="mt-6">
        <ReminderTemplatesCard />
      </div>

      <div className="mt-6">
        <FleetAlertsCard />
      </div>

      <div className="mt-6">
        <FleetSlaCard />
      </div>

      <div className="mt-6">
        <CpdCard />
      </div>

      <div className="mt-6">
        <CpdSuggestionsCard />
      </div>

      <div className="mt-6">
        <Card className="border-slate-200"><CardContent className="p-6">
          <div className="text-sm font-semibold text-[#1A3A5C]">Integrations</div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {[
              { name: 'Xero', status: 'Not connected', tone: 'text-slate-500' },
              { name: 'Stripe', status: 'Not connected', tone: 'text-slate-500' },
              { name: 'Microsoft 365 / Gmail', status: 'Coming in Phase 2', tone: 'text-amber-600' },
              { name: 'DHA ImmiAccount', status: 'Assisted-fill available', tone: 'text-[#2ECC9A]' },
              { name: 'VETASSESS API', status: 'Coming in Phase 2', tone: 'text-amber-600' },
            ].map((i) => (
              <div key={i.name} className="rounded-md border border-slate-200 p-3">
                <div className="font-medium text-[#1A3A5C]">{i.name}</div>
                <div className={`mt-1 text-xs ${i.tone}`}>{i.status}</div>
                <Button variant="outline" className="mt-2 h-8 w-full border-slate-200 text-xs">Configure</Button>
              </div>
            ))}
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

function statusTone(s) {
  if (s === 'verified') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s === 'pending' || s === 'not_started') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (s === 'failed' || s === 'temporary_failure') return 'bg-red-100 text-red-700 border-red-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

const DOMAIN_STEPS = [
  { n: 1, l: 'Add domain', d: 'Enter a subdomain like mail.yourfirm.com.au' },
  { n: 2, l: 'Copy DNS records', d: 'Paste them into GoDaddy / Cloudflare' },
  { n: 3, l: 'Verify', d: 'Click Verify once DNS has propagated (~5-15 min)' },
  { n: 4, l: 'Send from your domain', d: 'Set RESEND_FROM in backend/.env' },
];

function SenderDomainSteps({ active }) {
  return (
    <div className="mt-6 rounded-md border border-slate-200 bg-white p-3" data-testid="sender-domain-steps">
      <div className="grid gap-2 md:grid-cols-4">
        {DOMAIN_STEPS.map((s) => {
          const done = s.n < active;
          const current = s.n === active;
          return (
            <div
              key={s.n}
              data-testid={`sender-domain-step-${s.n}`}
              className={`rounded-md border p-2 transition-colors ${
                done ? 'border-[#2ECC9A]/40 bg-[#2ECC9A]/5'
                : current ? 'border-[#1A3A5C]/40 bg-[#1A3A5C]/5'
                : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                  done ? 'bg-[#2ECC9A] text-white'
                  : current ? 'bg-[#1A3A5C] text-white'
                  : 'bg-slate-200 text-slate-500'
                }`}>{done ? <CheckCircle2 className="h-3 w-3" /> : s.n}</div>
                <div className="text-xs font-semibold text-[#1A3A5C]">{s.l}</div>
              </div>
              <div className="mt-1 text-[10px] text-slate-500">{s.d}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SenderDomainCard() {
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState('');
  const [domain, setDomain] = React.useState(null);
  const [name, setName] = React.useState('');
  const [err, setErr] = React.useState('');
  const [copiedKey, setCopiedKey] = React.useState('');

  const load = React.useCallback(async () => {
    setErr('');
    try {
      const d = await senderDomain.get();
      setDomain(d && d.id ? d : null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load sender domain.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const addDomain = async (e) => {
    e.preventDefault(); if (!name.trim()) return;
    setBusy('create'); setErr('');
    try {
      const d = await senderDomain.create(name.trim().toLowerCase());
      setDomain(d);
      setName('');
    } catch (er) {
      setErr(er?.response?.data?.detail || 'Failed to add sender domain.');
    } finally { setBusy(''); }
  };

  const verify = async () => {
    setBusy('verify'); setErr('');
    try { const d = await senderDomain.verify(); setDomain(d); }
    catch (er) { setErr(er?.response?.data?.detail || 'Verification check failed.'); }
    finally { setBusy(''); }
  };

  const remove = async () => {
    if (!window.confirm('Remove this sender domain? Emails will fall back to onboarding@resend.dev.')) return;
    setBusy('remove'); setErr('');
    try { await senderDomain.remove(); setDomain(null); }
    catch (er) { setErr(er?.response?.data?.detail || 'Failed to remove domain.'); }
    finally { setBusy(''); }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text || '').then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? '' : k)), 1400);
    });
  };

  return (
    <Card className="border-slate-200" data-testid="sender-domain-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <Mail className="h-4 w-4 text-[#2ECC9A]" /> Sender domain (email deliverability)
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Send client emails from your own domain (e.g. <span className="font-mono">hello@mail.immipro.com.au</span>) instead of <span className="font-mono">onboarding@resend.dev</span>. Verified via Resend + AWS SES.
            </div>
          </div>
          {domain?.status && (
            <Badge variant="outline" className={`shrink-0 capitalize ${statusTone(domain.status)}`} data-testid="sender-domain-status">
              {domain.status.replace('_', ' ')}
            </Badge>
          )}
        </div>

        {loading ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : !domain ? (
          <>
            <SenderDomainSteps active={1} />
            <form onSubmit={addDomain} className="mt-4 space-y-3" data-testid="sender-domain-form">
            <div>
              <Label>Sending subdomain</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  data-testid="sender-domain-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="mail.immipro.com.au"
                  className="font-mono"
                />
                <Button
                  data-testid="sender-domain-submit"
                  type="submit"
                  disabled={!name.trim() || busy === 'create'}
                  className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"
                >
                  {busy === 'create' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                  Generate DNS records
                </Button>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Recommended: use a subdomain (like <span className="font-mono">mail.</span> or <span className="font-mono">send.</span>) so your root domain reputation stays isolated.
              </div>
            </div>
            {err && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="sender-domain-error">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {err}
              </div>
            )}
          </form>
          </>
        ) : (
          <div className="mt-6 space-y-4" data-testid="sender-domain-details">
            <SenderDomainSteps active={domain.status === 'verified' ? 4 : 2} />
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-[#F4F7FA] p-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Domain</div>
                <div className="mt-0.5 font-mono text-sm font-semibold text-[#1A3A5C]" data-testid="sender-domain-name">{domain.name}</div>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="sender-domain-verify"
                  onClick={verify}
                  disabled={busy === 'verify'}
                  variant="outline"
                  className="h-9 border-[#2ECC9A]/40 text-[#1A3A5C]"
                >
                  {busy === 'verify' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}
                  Verify DNS
                </Button>
                <Button
                  data-testid="sender-domain-remove"
                  onClick={remove}
                  disabled={busy === 'remove'}
                  variant="outline"
                  className="h-9 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Remove
                </Button>
              </div>
            </div>

            {domain.status === 'verified' ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="sender-domain-verified-callout">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Domain verified — you can now email anyone
                </div>
                <div className="mt-1 text-[11px] text-emerald-800">
                  Update <span className="font-mono">RESEND_FROM</span> in <span className="font-mono">backend/.env</span> to something like
                  <span className="font-mono"> ImmiPro &lt;hello@{domain.name}&gt;</span> and restart the backend. New invitations, refund receipts, and weekly digests will send from this address.
                </div>
              </div>
            ) : domain.provider_note && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                {domain.provider_note}
                <a
                  href="https://resend.com/domains" target="_blank" rel="noopener noreferrer"
                  data-testid="sender-domain-resend-link"
                  className="ml-1 inline-flex items-center gap-0.5 font-semibold underline hover:text-amber-950"
                >
                  Open resend.com/domains <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}

            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Name / Host</th>
                    <th className="px-3 py-2 font-medium">Value</th>
                    <th className="px-3 py-2 font-medium">Priority</th>
                    <th className="px-3 py-2 font-medium">TTL</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody data-testid="sender-domain-records">
                  {(domain.records || []).length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No records returned yet. Click Verify DNS.</td></tr>
                  )}
                  {(domain.records || []).map((r, i) => {
                    const key = `${r.type}-${r.name}-${i}`;
                    return (
                      <tr key={key} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-semibold text-[#1A3A5C]">{r.type}</td>
                        <td className="px-3 py-2 font-mono text-slate-700">{r.name || '—'}</td>
                        <td className="px-3 py-2 font-mono text-slate-700 break-all">{r.value || '—'}</td>
                        <td className="px-3 py-2 text-slate-500">{r.priority ?? '—'}</td>
                        <td className="px-3 py-2 text-slate-500">{r.ttl || 'Auto'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] capitalize ${statusTone(r.status)}`}>
                            {(r.status || '—').toString().replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => copy(r.value, key)}
                            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-[#1A3A5C]"
                            aria-label="Copy value"
                          >
                            {copiedKey === key ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                            {copiedKey === key ? 'Copied' : 'Copy'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-[11px] text-slate-500">
              Once all rows show <span className="font-medium text-emerald-600">verified</span>, set
              <span className="mx-1 font-mono">RESEND_FROM=&quot;ImmiPro &lt;hello@{domain.name}&gt;&quot;</span>
              in the backend env to send from this address.
            </div>

            {err && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="sender-domain-error">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {err}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DeadlineNudgeCard() {
  const [candidates, setCandidates] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([deadlineNudge.candidates(), deadlineNudge.history(20)])
      .then(([c, h]) => { setCandidates(c.candidates || []); setHistory(h.items || []); })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(load, [load]);

  const run = async (force) => {
    setBusy(true); setMsg(null);
    try {
      const summary = await deadlineNudge.run(force);
      setMsg({
        kind: summary.sent > 0 ? 'ok' : 'info',
        text: `Sweep complete — sent ${summary.sent}, skipped ${summary.skipped}, failed ${summary.failed} (of ${summary.scanned} scanned).`,
      });
      load();
    } catch (e) {
      setMsg({ kind: 'err', text: e?.response?.data?.detail || 'Sweep failed' });
    } finally { setBusy(false); }
  };

  const toneForDays = (d) => {
    if (d <= 3) return 'bg-red-100 text-red-700 border-red-200';
    if (d <= 5) return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-amber-100 text-amber-700 border-amber-200';
  };

  return (
    <Card className="border-slate-200" data-testid="deadline-nudge-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <BellRing className="h-4 w-4 text-[#2ECC9A]" /> Client deadline nudges
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Auto-emails clients when a <span className="font-medium">medical / police / health / AFP</span> deadline is 7 days away. Case owner is CC&rsquo;d. Runs daily at server boot + 24h thereafter.
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="deadline-nudge-refresh"
              onClick={load}
              variant="outline"
              className="h-9 border-slate-200"
            >
              <RefreshCw className="mr-1 h-4 w-4" /> Refresh
            </Button>
            <Button
              data-testid="deadline-nudge-run"
              onClick={() => run(false)}
              disabled={busy || candidates.filter((c) => !c.already_sent).length === 0}
              className="h-9 gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Broadcast now ({candidates.filter((c) => !c.already_sent).length})
            </Button>
          </div>
        </div>

        {msg && (
          <div
            data-testid="deadline-nudge-msg"
            className={`mt-4 rounded-md p-3 text-sm ${msg.kind === 'ok' ? 'border border-emerald-200 bg-emerald-50 text-emerald-800' : msg.kind === 'err' ? 'border border-red-200 bg-red-50 text-red-700' : 'border border-slate-200 bg-slate-50 text-slate-700'}`}
          >
            {msg.text}
          </div>
        )}

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Candidates ({candidates.length})</div>
            {loading ? (
              <div className="mt-3 flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
            ) : candidates.length === 0 ? (
              <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                No clients with critical deadlines in the next 7 days.
              </div>
            ) : (
              <div className="mt-3 space-y-2" data-testid="deadline-nudge-candidates">
                {candidates.map((c) => (
                  <div key={c.matter_ref} className="rounded-md border border-slate-200 p-3">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#1A3A5C]">{c.applicant} <span className="text-xs text-slate-400">· {c.matter_ref}</span></div>
                        <div className="mt-0.5 text-xs text-slate-500 truncate">{c.deadline_label}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge variant="outline" className={`capitalize ${toneForDays(c.days_until)}`}>
                          {c.days_until === 0 ? 'today' : `${c.days_until}d`}
                        </Badge>
                        {c.already_sent && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" title="Already emailed" />}
                      </div>
                    </div>
                    <div className="mt-1 text-[10px] font-mono text-slate-400 truncate">→ {c.applicant_email} · cc {c.case_owner_email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recent sends ({history.length})</div>
            {history.length === 0 ? (
              <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                No emails sent yet.
              </div>
            ) : (
              <div className="mt-3 max-h-72 space-y-1 overflow-auto" data-testid="deadline-nudge-history">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md bg-slate-50 p-2 text-[11px]">
                    <div className="min-w-0 truncate">
                      <span className="font-medium text-[#1A3A5C]">{h.matter_ref}</span>
                      <span className="ml-1 text-slate-500 truncate">· {h.deadline_label}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant="outline" className={`capitalize ${h.status === 'sent' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
                        {h.status}
                      </Badge>
                      <span className="text-slate-400">{new Date(h.sent_at).toLocaleTimeString('en-AU')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


const CPD_TYPES = [
  { v: 'seminar', l: 'Seminar' }, { v: 'webinar', l: 'Webinar' },
  { v: 'conference', l: 'Conference' }, { v: 'workshop', l: 'Workshop' },
  { v: 'self_study', l: 'Self-study' }, { v: 'reading', l: 'Reading' },
  { v: 'teaching', l: 'Teaching' }, { v: 'writing_article', l: 'Writing article' },
  { v: 'mentoring', l: 'Mentoring' }, { v: 'exam', l: 'Exam' }, { v: 'other', l: 'Other' },
];

function CpdCard() {
  const [summary, setSummary] = React.useState(null);
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [form, setForm] = React.useState({
    activity_type: 'seminar', title: '', provider: '', points: 1,
    activity_date: new Date().toISOString().slice(0, 10),
  });

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([cpdApi.summary(), cpdApi.list()])
      .then(([s, list]) => { setSummary(s); setEntries(list); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      await cpdApi.create({ ...form, points: Number(form.points) });
      setForm((f) => ({ ...f, title: '', provider: '', points: 1 }));
      load();
    } catch (er) {
      setErr(er?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await cpdApi.remove(id); load(); } catch { /* noop */ }
  };

  const pct = summary?.progress_pct || 0;
  const bar = pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-[#2ECC9A]' : 'bg-amber-500';

  return (
    <Card className="border-slate-200" data-testid="cpd-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <GraduationCap className="h-4 w-4 text-[#2ECC9A]" /> CPD tracker (OMARA)
            </div>
            <div className="mt-1 text-xs text-slate-500">
              OMARA requires <b>10 CPD points</b> per registration year (1 Jul → 30 Jun). Feeds the Firm Health Score.
            </div>
          </div>
          {summary && (
            <div className="text-right">
              <div className="text-2xl font-bold text-[#1A3A5C]" data-testid="cpd-points-earned">{summary.points_earned}<span className="text-sm text-slate-400">/{summary.target_points}</span></div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Year {summary.cpd_year}</div>
              <a
                href={cpdApi.summaryPdfUrl()}
                target="_blank"
                rel="noreferrer"
                data-testid="cpd-download-pdf"
                onClick={(e) => {
                  e.preventDefault();
                  const t = localStorage.getItem('immipro_token');
                  fetch(cpdApi.summaryPdfUrl(), { headers: t ? { Authorization: `Bearer ${t}` } : {} })
                    .then((r) => r.blob())
                    .then((blob) => {
                      const url = URL.createObjectURL(blob);
                      window.open(url, '_blank');
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    })
                    .catch(() => toast.error('Could not download CPD summary'));
                }}
                className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#2ECC9A] hover:underline"
              >
                <FileText className="h-3 w-3" /> Download PDF
              </a>
              <CpdAutoLogTrigger onLogged={load} />
            </div>
          )}
        </div>

        {summary && (
          <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} data-testid="cpd-progress-bar" />
            </div>
            <div className="mt-1 text-[11px] text-slate-500">
              {summary.points_remaining > 0 ? `${summary.points_remaining} pts remaining to hit target` : 'Target hit — well done.'}
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-4 flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : (
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <form onSubmit={submit} className="space-y-3" data-testid="cpd-form">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Log an activity</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                    <SelectTrigger data-testid="cpd-type" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{CPD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Points</Label>
                  <Input data-testid="cpd-points" type="number" min="0.5" max="20" step="0.5" value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} className="mt-1 h-9" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Title</Label>
                <Input data-testid="cpd-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. MIA National Conference 2026" className="mt-1 h-9" required />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Provider (optional)</Label>
                  <Input data-testid="cpd-provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">Activity date</Label>
                  <Input data-testid="cpd-date" type="date" value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} className="mt-1 h-9" required />
                </div>
              </div>
              {err && <div className="rounded-md bg-red-50 p-2 text-[11px] text-red-700">{err}</div>}
              <Button data-testid="cpd-submit" type="submit" disabled={busy || !form.title} className="gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add entry
              </Button>
            </form>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recent entries ({entries.length})</div>
              {entries.length === 0 ? (
                <div className="mt-3 rounded-md border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">
                  No CPD entries yet — log one on the left.
                </div>
              ) : (
                <div className="mt-3 max-h-72 space-y-2 overflow-auto" data-testid="cpd-entries">
                  {entries.map((e) => (
                    <CpdEntryRow key={e.id} entry={e} onChange={load} onRemove={() => remove(e.id)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function CpdAutoLogTrigger({ onLogged }) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [suggestion, setSuggestion] = React.useState(null);

  const extract = async () => {
    if (text.trim().length < 20) { toast.error('Paste more of the email'); return; }
    setBusy(true); setSuggestion(null);
    try {
      const r = await cpdExtract.fromEmail(text);
      setSuggestion(r.suggested);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Extraction failed');
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await cpdApi.create({ ...suggestion, points: Number(suggestion.points) });
      toast.success(`Logged "${suggestion.title}"`);
      setOpen(false); setText(''); setSuggestion(null);
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="cpd-autolog-btn"
              className="mt-1 ml-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#2ECC9A] hover:underline">
        Auto-log from email
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6"
             onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="cpd-autolog-dialog">
            <div className="mb-3 font-serif text-lg font-semibold text-[#1A3A5C]">Auto-log CPD from an email</div>
            <p className="mb-3 text-xs text-slate-500">
              Paste the webinar-registration or attendance-confirmation email below. Our LLM extracts the title,
              provider, hours and date — you confirm with one click.
            </p>
            <textarea rows={7} value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="Paste the email body here…"
                      data-testid="cpd-autolog-text"
                      className="w-full rounded-md border border-slate-200 p-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#2ECC9A]/30" />
            {!suggestion ? (
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={() => setOpen(false)} className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="button" onClick={extract} disabled={busy || text.trim().length < 20}
                        data-testid="cpd-autolog-extract"
                        className="rounded-md bg-[#1A3A5C] px-3 py-1 text-xs font-semibold text-white hover:bg-[#1A3A5C]/90 disabled:opacity-50">
                  {busy ? 'Extracting…' : 'Extract with AI'}
                </button>
              </div>
            ) : (
              <div className="mt-3 space-y-2 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-3" data-testid="cpd-autolog-suggestion">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-[#2ECC9A]">AI suggestion — edit if needed</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <label>Title<input value={suggestion.title} onChange={(e) => setSuggestion({ ...suggestion, title: e.target.value })} data-testid="cpd-autolog-title" className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1" /></label>
                  <label>Provider<input value={suggestion.provider || ''} onChange={(e) => setSuggestion({ ...suggestion, provider: e.target.value })} className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1" /></label>
                  <label>Type<input value={suggestion.activity_type} onChange={(e) => setSuggestion({ ...suggestion, activity_type: e.target.value })} className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1" /></label>
                  <label>Points<input type="number" step="0.5" value={suggestion.points} onChange={(e) => setSuggestion({ ...suggestion, points: e.target.value })} data-testid="cpd-autolog-points" className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1" /></label>
                  <label>Date<input type="date" value={suggestion.activity_date} onChange={(e) => setSuggestion({ ...suggestion, activity_date: e.target.value })} className="mt-0.5 w-full rounded border border-slate-200 px-2 py-1" /></label>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={() => setSuggestion(null)} className="rounded-md border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50">Try again</button>
                  <button type="button" onClick={save} disabled={busy}
                          data-testid="cpd-autolog-save"
                          className="rounded-md bg-[#2ECC9A] px-3 py-1 text-xs font-semibold text-white hover:bg-[#2ECC9A]/90 disabled:opacity-50">
                    {busy ? 'Saving…' : 'Log this activity'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}




function CpdEntryRow({ entry, onChange, onRemove }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const type = CPD_TYPES.find((t) => t.v === entry.activity_type)?.l || entry.activity_type;

  const pickFile = () => fileRef.current?.click();
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setErr('');
    try { await cpdApi.uploadEvidence(entry.id, f); onChange?.(); }
    catch (er) { setErr(er?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); e.target.value = ''; }
  };
  const download = () => window.open(cpdApi.evidenceUrl(entry.id), '_blank');

  const kb = entry.evidence_size ? Math.max(1, Math.round(entry.evidence_size / 1024)) : null;

  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 text-xs" data-testid={`cpd-entry-${entry.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-[#1A3A5C]">{entry.title}</div>
          <div className="text-[10px] text-slate-500">{type} · {entry.activity_date} · {entry.provider || 'self-recorded'}</div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge variant="outline" className="border-[#2ECC9A]/40 bg-[#2ECC9A]/10 text-[10px] text-[#1A3A5C]">
            <Target className="mr-1 h-3 w-3" /> {entry.points} pt{entry.points !== 1 ? 's' : ''}
          </Badge>
          <button onClick={onRemove} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 border-t border-slate-100 pt-2">
        {entry.evidence_filename ? (
          <>
            <button
              onClick={download}
              data-testid={`cpd-evidence-view-${entry.id}`}
              className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-1 text-[10px] text-[#1A3A5C] hover:bg-slate-100"
              title={`${entry.evidence_filename} · ${kb}KB`}
            >
              <FileText className="h-3 w-3" /> {entry.evidence_filename}
              <span className="text-slate-400">· {kb}KB</span>
            </button>
            <button
              onClick={pickFile}
              className="text-[10px] text-slate-400 hover:text-[#1A3A5C]"
              title="Replace"
            >Replace</button>
          </>
        ) : (
          <button
            onClick={pickFile}
            disabled={busy}
            data-testid={`cpd-evidence-upload-${entry.id}`}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-slate-300 px-2 py-1 text-[10px] text-slate-500 hover:border-[#2ECC9A] hover:bg-[#2ECC9A]/5 hover:text-[#1A3A5C]"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
            Attach evidence
          </button>
        )}
        <input ref={fileRef} type="file" onChange={upload} className="hidden" accept="application/pdf,image/*" data-testid={`cpd-evidence-file-${entry.id}`} />
        {err && <span className="ml-auto text-[10px] text-red-600">{err}</span>}
      </div>
    </div>
  );
}


// --------------- AI-powered CPD Suggester card ---------------
// Cross-references the RMA's CPD gap with the last few Policy Pulse items
// via the Emergent LLM. Suggestions cached server-side for 24h.
function CpdSuggestionsCard() {
  const [state, setState] = React.useState({ items: [], generated_at: null, cached: false, source: 'llm', points_remaining: null });
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [themes, setThemes] = React.useState({ themes: [], likes_count: 0, source: 'empty' });

  const loadThemes = React.useCallback(() => {
    cpdApi.suggestionThemes().then(setThemes).catch(() => {});
  }, []);

  const load = React.useCallback((refresh = false) => {
    const setter = refresh ? setRefreshing : setLoading;
    setter(true);
    cpdApi.suggestions(refresh)
      .then((r) => setState(r))
      .catch(() => setState((s) => ({ ...s, items: [] })))
      .finally(() => setter(false));
  }, []);

  React.useEffect(() => { load(false); loadThemes(); }, [load, loadThemes]);

  return (
    <Card className="border-slate-200" data-testid="cpd-suggestions-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
              <Sparkles className="h-4 w-4 text-[#2ECC9A]" /> AI-recommended CPD activities
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Personalised suggestions cross-referenced against recent DHA/OMARA policy trends and your remaining CPD points.
              {state.points_remaining !== null && (
                <span className="ml-1">You still need <b>{state.points_remaining} pts</b> this year.</span>
              )}
            </div>
          </div>
          <Button
            data-testid="cpd-suggestions-refresh"
            variant="outline"
            size="sm"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="h-8 gap-1 border-slate-200 text-xs"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>

        <ThemesPanel themes={themes.themes} likesCount={themes.likes_count} />

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : state.items.length === 0 ? (
          <div className="mt-6 rounded-md border border-dashed border-slate-200 p-6 text-center text-xs text-slate-500">
            No suggestions available right now. Try again in a few minutes.
          </div>
        ) : (
          <div className="mt-5 grid gap-3 md:grid-cols-3" data-testid="cpd-suggestions-list">
            {state.items.map((s, idx) => (
              <SuggestionCard
                key={`${s.title}-${idx}`}
                idx={idx}
                item={s}
                onHidden={(title) => setState((st) => ({ ...st, items: st.items.filter((x) => x.title !== title) }))}
                onLiked={(title) => { setState((st) => ({ ...st, items: st.items.map((x) => x.title === title ? { ...x, liked: !x.liked } : x) })); loadThemes(); }}
              />
            ))}
          </div>
        )}

        {state.generated_at && (
          <div className="mt-4 flex items-center gap-2 text-[10px] text-slate-400">
            <span>Generated {new Date(state.generated_at).toLocaleString('en-AU')}</span>
            {state.cached && <Badge variant="outline" className="h-4 border-slate-200 px-1 text-[9px] text-slate-500">cached</Badge>}
            {state.source === 'fallback' && <Badge variant="outline" className="h-4 border-amber-200 bg-amber-50 px-1 text-[9px] text-amber-700">fallback</Badge>}
            <span className="ml-auto text-slate-400">Thumbs-up or hide to teach the AI your preferences.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function SuggestionCard({ idx, item, onHidden, onLiked }) {
  const [busy, setBusy] = React.useState(null); // 'up' | 'hide' | null
  const [hidden, setHidden] = React.useState(false);
  const [liked, setLiked] = React.useState(!!item.liked);

  const vote = async (kind) => {
    if (busy) return;
    setBusy(kind);
    try {
      if (kind === 'up') {
        const nextLiked = !liked;
        await cpdApi.suggestionFeedback(item.title, nextLiked ? 'up' : 'clear');
        setLiked(nextLiked);
        onLiked?.(item.title);
      } else {
        await cpdApi.suggestionFeedback(item.title, 'hide');
        setHidden(true);
        setTimeout(() => onHidden?.(item.title), 200);
      }
    } catch { /* noop */ }
    finally { setBusy(null); }
  };

  if (hidden) return null;

  return (
    <div
      data-testid={`cpd-suggestion-${idx}`}
      className="flex flex-col rounded-md border border-slate-200 bg-gradient-to-br from-white to-[#2ECC9A]/5 p-3 transition-opacity"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 text-sm font-semibold text-[#1A3A5C]">{item.title}</div>
        <Badge variant="outline" className="shrink-0 border-[#2ECC9A]/40 bg-[#2ECC9A]/10 text-[10px] text-[#1A3A5C]">
          <Target className="mr-1 h-3 w-3" /> {item.points} pt{item.points !== 1 ? 's' : ''}
        </Badge>
      </div>
      {item.provider && <div className="mt-0.5 text-[11px] text-slate-500">{item.provider}</div>}
      <div className="mt-2 flex-1 text-xs text-slate-600">{item.why}</div>

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`cpd-suggestion-link-${idx}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-[#1A3A5C] hover:text-[#2ECC9A]"
          >
            View activity <ExternalLink className="h-3 w-3" />
          </a>
        ) : <span />}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => vote('up')}
            disabled={!!busy}
            data-testid={`cpd-suggestion-up-${idx}`}
            aria-pressed={liked}
            title={liked ? 'Thumbed up — click to clear' : 'Thumbs up — show more like this'}
            className={`inline-flex h-6 w-6 items-center justify-center rounded-full border transition-colors ${
              liked
                ? 'border-[#2ECC9A] bg-[#2ECC9A]/15 text-[#2ECC9A]'
                : 'border-slate-200 text-slate-400 hover:border-[#2ECC9A] hover:text-[#2ECC9A]'
            } disabled:opacity-50`}
          >
            {busy === 'up' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" />}
          </button>
          <button
            type="button"
            onClick={() => vote('hide')}
            disabled={!!busy}
            data-testid={`cpd-suggestion-hide-${idx}`}
            title="Hide — don't suggest this again"
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-50"
          >
            {busy === 'hide' ? <Loader2 className="h-3 w-3 animate-spin" /> : <EyeOff className="h-3 w-3" />}
          </button>
        </div>
      </div>
    </div>
  );
}



// --------------- Themes panel (top 3 CPD interests from thumbs-ups) ---------------
function ThemesPanel({ themes, likesCount }) {
  if (!themes || themes.length === 0) {
    return (
      <div
        data-testid="cpd-themes-empty"
        className="mt-4 rounded-md border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-[11px] text-slate-500"
      >
        <ThumbsUp className="mr-1.5 inline h-3 w-3 text-slate-400" />
        Thumbs-up a suggestion or two and your top CPD themes will show up here.
      </div>
    );
  }

  return (
    <div
      data-testid="cpd-themes-panel"
      className="mt-4 rounded-md border border-[#2ECC9A]/20 bg-gradient-to-r from-[#2ECC9A]/5 to-transparent px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#1A3A5C]">
          <Sparkles className="h-3 w-3 text-[#2ECC9A]" /> Your top CPD themes
        </div>
        {themes.map((t, i) => (
          <Badge
            key={t.label}
            data-testid={`cpd-theme-${i}`}
            variant="outline"
            className="border-[#2ECC9A]/40 bg-white text-[11px] text-[#1A3A5C]"
          >
            {t.label}
            <span className="ml-1 text-slate-400">· {t.count}</span>
          </Badge>
        ))}
        <span className="ml-auto text-[10px] text-slate-400">from {likesCount} thumbs-up</span>
      </div>
    </div>
  );
}


// --------------- Notification preferences ---------------
function NotificationPrefsCard() {
  const [mode, setMode] = React.useState('instant');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    notifPrefs.get().then((r) => setMode(r.skillselect_alert_mode || 'instant')).finally(() => setLoading(false));
  }, []);

  const change = async (next) => {
    setMode(next);
    setSaving(true);
    setSaved(false);
    try {
      await notifPrefs.set(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally { setSaving(false); }
  };

  const OPTS = [
    { v: 'instant', l: 'Instant', d: 'Fire a firm alert the moment a new round shifts min-points.' },
    { v: 'digest',  l: 'Weekly digest', d: 'Bundle round movements into the Monday firm-health email.' },
    { v: 'off',     l: 'Off', d: 'Never notify me about SkillSelect round changes.' },
  ];

  return (
    <Card className="border-slate-200" data-testid="notif-prefs-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#1A3A5C]">
          <Bell className="h-4 w-4 text-[#2ECC9A]" /> SkillSelect round notifications
        </div>
        <div className="mt-1 text-xs text-slate-500">
          Choose how you want to be notified when the min-points cut-off changes for a subclass you have matters in.
        </div>

        {loading ? (
          <div className="mt-6 flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
        ) : (
          <div className="mt-4 grid gap-2 md:grid-cols-3" data-testid="notif-prefs-options">
            {OPTS.map((o) => (
              <button
                key={o.v}
                data-testid={`notif-pref-${o.v}`}
                onClick={() => change(o.v)}
                disabled={saving}
                className={`rounded-md border p-3 text-left transition-colors ${
                  mode === o.v
                    ? 'border-[#2ECC9A] bg-[#2ECC9A]/5'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-[#1A3A5C]">{o.l}</div>
                  {mode === o.v && <CheckCircle2 className="h-4 w-4 text-[#2ECC9A]" />}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">{o.d}</div>
              </button>
            ))}
          </div>
        )}

        {saved && (
          <div className="mt-3 inline-flex items-center gap-1 rounded-md bg-[#2ECC9A]/10 px-2 py-1 text-[11px] text-[#1A3A5C]" data-testid="notif-prefs-saved">
            <CheckCircle2 className="h-3 w-3 text-[#2ECC9A]" /> Saved
          </div>
        )}
      </CardContent>
    </Card>
  );
}
